export type Wallet = {
    name: string
    pubkey: string
}

export type Viewer = {
    is_auth: boolean
    email_confirmed: boolean
    id: number
    username: string
    email: string
    wallets: Wallet[]
    is_staff: boolean
    is_developer: boolean
    is_marketing: boolean
    is_executive: boolean
    beta_applicant: boolean
    is_holder: boolean
    twitter_connected: boolean
    wallet_guide_seen: boolean
    roles: string[]
    /** A staff hold on this account's cash. Buying and withdrawing are refused server-side while true. */
    funds_frozen?: boolean
    /** Packs is published to players (the kill switch's "open to players"). Staff see it regardless. */
    packs_public?: boolean
    /** The player marketplace (/trade) is open to players. Staff see it regardless. */
    marketplace_public?: boolean
    /** A Team Tools capability was granted from the admin, possibly with no role flag at all. */
    has_team_tools_grant?: boolean
}

export const AnonymousViewer = {
    is_auth: false,
}

export interface SignupCredentials {
    username: string
    email: string
    password1: string
    password2: string
    /** Solved Cloudflare Turnstile token. Absent when no site key is configured. */
    captchaToken?: string
}

export interface LoginCredentials {
    username: string
    password: string
}

export interface ValidationError {
    message: string
    code: string
}

export interface SignupBadRequest {
    username?: ValidationError[]
    email?: ValidationError[]
    password1?: ValidationError[]
    password2?: ValidationError[]
    __all__?: ValidationError[]
}

export interface LoginBadRequest {
    username?: ValidationError[]
    password?: ValidationError[]
    __all__?: ValidationError[]
}

/**
 * Outcome of a password submission.
 *
 * A staff account with an enrolled device gets no session from the password
 * alone -- the backend parks the login and answers 401 {"mfa_required": true}.
 * That is a successful first step, not an error, so it is modelled here rather
 * than thrown.
 */
export interface LoginResult {
    mfaRequired: boolean
}

export interface MfaStatus {
    is_staff_account: boolean
    enrolled: boolean
    session_verified: boolean
    /** Whether the server would actually refuse this session. False when the
     *  MFA kill switch is off, when the account is not staff, or when this
     *  session has already cleared the second factor. */
    verification_required?: boolean
    enforcement_active: boolean
    unused_recovery_codes: number
}

export interface MfaEnrolment {
    /** Base32 secret, for manual entry when a QR cannot be scanned. */
    secret: string
    provisioning_uri: string
    /** SVG QR as a data: URI, so it can go straight into an <img src>. */
    qr_data_uri: string
}

export interface MfaError {
    message: string
    status?: number
}

export interface ActivationBadRequest {
    code?: ValidationError[]
    __all__?: ValidationError[]
}

export interface ResendActivationEmailBadRequest {
    email_cooldown?: ValidationError[]
    __all__?: ValidationError[]
}

export interface PasswordResetBadRequest {
    email?: ValidationError[]
    __all__?: ValidationError[]
}

export interface PasswordSetBadRequest {
    new_password1?: ValidationError[]
    new_password2?: ValidationError[]
    token: ValidationError[]
    __all__?: ValidationError[]
}
